/**
 * ReplanificationForm - Formulaire pour replanifier une optimisation
 */
import { useState } from 'react'
import { OptimizationRun, Item3D } from '../../types/optimization'
import { PlusIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import ItemAdvancedFields from './ItemAdvancedFields'
import {
  ItemAdvancedFormValues,
  emptyItemAdvancedValues,
  validateItemAdvancedFields,
  applyAdvancedFieldsToItem,
  formatItemAdvancedTags,
} from '../../lib/itemFormHelpers'

interface ReplanificationFormProps {
  previousRun: OptimizationRun
  onSubmit: (data: ReplanificationData) => void
  isLoading?: boolean
}

export interface ReplanificationData {
  incident_type?: string
  incident_description?: string
  excluded_item_ids?: string[]
  added_items?: Item3D[]
  removed_items?: string[]
  constraints_changes?: Record<string, unknown>
  reason?: string
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'

const sectionClass = 'rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-6'

export default function ReplanificationForm({
  previousRun,
  onSubmit,
  isLoading = false,
}: ReplanificationFormProps) {
  const [incidentType, setIncidentType] = useState<string>('item_unavailable')
  const [incidentDescription, setIncidentDescription] = useState<string>('')
  const [excludedItemIds, setExcludedItemIds] = useState<string[]>([])
  const [addedItems, setAddedItems] = useState<Item3D[]>([])
  const [removedItemIds] = useState<string[]>([])
  const [reason, setReason] = useState<string>('')

  const [newItemForm, setNewItemForm] = useState<Partial<Item3D>>({ quantity: 1 })
  const [newItemAdvanced, setNewItemAdvanced] = useState<ItemAdvancedFormValues>(emptyItemAdvancedValues())
  const [showNewItemForm, setShowNewItemForm] = useState(false)

  const handleAddExcludedItem = (itemId: string) => {
    if (!excludedItemIds.includes(itemId)) {
      setExcludedItemIds([...excludedItemIds, itemId])
    }
  }

  const handleRemoveExcludedItem = (itemId: string) => {
    setExcludedItemIds(excludedItemIds.filter((id) => id !== itemId))
  }

  const handleAddNewItem = () => {
    if (newItemForm.id && newItemForm.length && newItemForm.width && newItemForm.height && newItemForm.weight) {
      const advancedError = validateItemAdvancedFields(newItemAdvanced)
      if (advancedError) {
        alert(advancedError)
        return
      }

      const baseItem: Item3D = {
        id: newItemForm.id,
        description: newItemForm.description,
        length: newItemForm.length,
        width: newItemForm.width,
        height: newItemForm.height,
        weight: newItemForm.weight,
        quantity: newItemForm.quantity || 1,
        stackable: newItemForm.stackable,
        fragile: newItemForm.fragile,
        rotatable: newItemForm.rotatable,
      }
      const item = applyAdvancedFieldsToItem(baseItem, newItemAdvanced)

      setAddedItems([...addedItems, item])
      setNewItemForm({ quantity: 1 })
      setNewItemAdvanced(emptyItemAdvancedValues())
      setShowNewItemForm(false)
    }
  }

  const handleRemoveAddedItem = (itemId: string) => {
    setAddedItems(addedItems.filter((item) => item.id !== itemId))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      incident_type: incidentType,
      incident_description: incidentDescription,
      excluded_item_ids: excludedItemIds,
      added_items: addedItems,
      removed_items: removedItemIds,
      reason,
    })
  }

  const allCurrentItems = previousRun.input.items
  const loadedItemIds = new Set(
    (previousRun.result?.placements || []).flatMap((p) => {
      const ids = []
      const itemId = p.item_id || p.itemId
      if (itemId) {
        ids.push(itemId)
        if (itemId.includes('_')) {
          const parts = itemId.split('_')
          const lastPart = parts[parts.length - 1]
          if (!isNaN(Number(lastPart))) {
            ids.push(parts.slice(0, -1).join('_'))
          }
        }
      }
      if ((p as any).metadata?.source_id) {
        ids.push(String((p as any).metadata.source_id))
      }
      return ids
    }).filter(Boolean)
  )
  const loadedItems = allCurrentItems.filter((item) => loadedItemIds.has(item.id))

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className={sectionClass}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Type d&apos;incident</h3>
        <div className="space-y-3">
          {[
            { value: 'item_unavailable', label: 'Article indisponible' },
            { value: 'item_added', label: 'Article ajouté' },
            { value: 'urgency_change', label: 'Changement urgence' },
            { value: 'constraint_change', label: 'Changement contrainte' },
            { value: 'other', label: 'Autre' },
          ].map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-3 p-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-blue-300 cursor-pointer transition"
            >
              <input
                type="radio"
                value={option.value}
                checked={incidentType === option.value}
                onChange={(e) => setIncidentType(e.target.value)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-700 dark:text-gray-300">{option.label}</span>
            </label>
          ))}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description de l&apos;incident</label>
          <textarea
            value={incidentDescription}
            onChange={(e) => setIncidentDescription(e.target.value)}
            placeholder="Expliquer ce qui s'est passé et pourquoi il faut replanifier..."
            className={inputClass}
            rows={3}
          />
        </div>
      </div>

      <div className={sectionClass}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Articles à exclure</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Sélectionner les articles qui ne doivent pas être replanifiés
        </p>

        {loadedItems.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {loadedItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{item.id}</div>
                  {item.description && <div className="text-xs text-gray-600 dark:text-gray-400">{item.description}</div>}
                </div>
                <button
                  type="button"
                  onClick={() => handleAddExcludedItem(item.id)}
                  disabled={excludedItemIds.includes(item.id)}
                  className={`px-3 py-1 rounded text-xs font-medium transition ${
                    excludedItemIds.includes(item.id)
                      ? 'bg-red-600 text-white'
                      : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-red-50 hover:text-red-700 hover:border-red-300'
                  }`}
                >
                  {excludedItemIds.includes(item.id) ? 'Exclu' : 'Exclure'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">Aucun article chargé</div>
        )}

        {excludedItemIds.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="text-sm text-red-800 mb-2 font-semibold">
              {excludedItemIds.length} article(s) à exclure
            </div>
            <div className="flex flex-wrap gap-2">
              {excludedItemIds.map((itemId) => (
                <div
                  key={itemId}
                  className="flex items-center gap-2 bg-red-100 text-red-800 border border-red-200 px-3 py-1 rounded-full text-sm"
                >
                  <span>{itemId}</span>
                  <button type="button" onClick={() => handleRemoveExcludedItem(itemId)} className="hover:text-red-600">
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={sectionClass}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Articles à ajouter</h3>

        {addedItems.length > 0 && (
          <div className="mb-4 space-y-2 max-h-32 overflow-y-auto">
            {addedItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md"
              >
                <div className="flex-1 text-sm">
                  <div className="font-medium text-green-800">{item.id}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {item.length}×{item.width}×{item.height} mm, {item.weight} kg
                    {item.quantity && item.quantity > 1 && ` (×${item.quantity})`}
                    {formatItemAdvancedTags(item).length > 0 && (
                      <span className="block mt-0.5">{formatItemAdvancedTags(item).join(' • ')}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveAddedItem(item.id)}
                  className="p-1 hover:bg-red-100 rounded transition"
                >
                  <TrashIcon className="w-4 h-4 text-red-600" />
                </button>
              </div>
            ))}
          </div>
        )}

        {showNewItemForm ? (
          <div className="space-y-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md mb-4">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="ID article"
                value={newItemForm.id || ''}
                onChange={(e) => setNewItemForm({ ...newItemForm, id: e.target.value })}
                className={inputClass}
                required
              />
              <input
                type="text"
                placeholder="Description"
                value={newItemForm.description || ''}
                onChange={(e) => setNewItemForm({ ...newItemForm, description: e.target.value })}
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number"
                placeholder="Longueur (mm)"
                value={newItemForm.length || ''}
                onChange={(e) => setNewItemForm({ ...newItemForm, length: parseInt(e.target.value) })}
                className={inputClass}
                required
              />
              <input
                type="number"
                placeholder="Largeur (mm)"
                value={newItemForm.width || ''}
                onChange={(e) => setNewItemForm({ ...newItemForm, width: parseInt(e.target.value) })}
                className={inputClass}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number"
                placeholder="Hauteur (mm)"
                value={newItemForm.height || ''}
                onChange={(e) => setNewItemForm({ ...newItemForm, height: parseInt(e.target.value) })}
                className={inputClass}
                required
              />
              <input
                type="number"
                placeholder="Poids (kg)"
                value={newItemForm.weight || ''}
                onChange={(e) => setNewItemForm({ ...newItemForm, weight: parseInt(e.target.value) })}
                className={inputClass}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number"
                placeholder="Quantité"
                value={newItemForm.quantity || 1}
                onChange={(e) => setNewItemForm({ ...newItemForm, quantity: parseInt(e.target.value) })}
                className={inputClass}
                min={1}
              />
              <label className="flex items-center gap-2 px-3 py-2">
                <input
                  type="checkbox"
                  checked={newItemForm.stackable || false}
                  onChange={(e) => setNewItemForm({ ...newItemForm, stackable: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Empilable</span>
              </label>
            </div>
            <ItemAdvancedFields
              values={newItemAdvanced}
              onChange={setNewItemAdvanced}
              idPrefix="replan-item"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleAddNewItem}
                className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition"
              >
                Ajouter
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewItemForm(false)
                  setNewItemAdvanced(emptyItemAdvancedValues())
                }}
                className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white dark:text-white rounded-md font-medium transition"
              >
                Annuler
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowNewItemForm(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-md font-medium transition"
          >
            <PlusIcon className="w-4 h-4" />
            Ajouter un article
          </button>
        )}
      </div>

      <div className={sectionClass}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Raison de la replanification</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Expliquer pourquoi cette replanification est nécessaire..."
          className={inputClass}
          rows={3}
        />
      </div>

      <div className="flex gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-yellow-800">
          <p className="font-semibold mb-1">Important</p>
          <p>
            Cette replanification créera une nouvelle optimisation. L&apos;optimisation précédente sera
            conservée à titre de référence.
          </p>
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-lg transition"
        data-e2e="replan-submit"
      >
        {isLoading ? 'Replanification en cours...' : 'Soumettre la replanification'}
      </button>
    </form>
  )
}
